import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Container, Row, Col, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  FaCogs, FaArrowRight, FaChartLine, FaHeadset, FaSimCard, 
  FaChevronLeft, FaChevronRight, FaTrophy, FaUsers, 
  FaBullseye, FaRocket, FaLightbulb, FaGlobeAmericas
} from 'react-icons/fa';
import { ParticlesBackground } from '../../components/ui'; 
import { useScrollAnimation } from '../../hooks';
import { useLanguage } from '../../contexts/LanguageContext'; 
import { Helmet } from 'react-helmet-async';


const API_BASE_URL = 'https://www.s2i.com.tn';

const Counter = ({ value, duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  useEffect(() => {
    if (isInView) {
      let start = 0;
      const end = parseInt(value);
      if (start === end) return;
      let timer = setInterval(() => {
        start += 1;
        setCount(start);
        if (start === end) clearInterval(timer);
      }, (duration * 1000) / end);
      return () => clearInterval(timer);
    }
  }, [isInView, value, duration]);
  return <span ref={ref}>{count}</span>;
};

const Home = () => {
  const { t, lang } = useLanguage(); 
  const navigate = useNavigate();
  
  const [setPartnersRef] = useScrollAnimation(0.1);
  const [setClientsRef] = useScrollAnimation(0.1);
  const [setCertifRef] = useScrollAnimation(0.1);
  const [setInterRef] = useScrollAnimation(0.1);

  const [partners, setPartners] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState({ type: '', msg: '' });

  // 1. Les certifications
  const certifications = [
    { name: "DELL", logo: "/images/dell.png" },
    { name: "KYOCERA", logo: "/images/kyocera.png" },
    { name: "PNY", logo: "/images/pnyy.png" },
    { name: "KUBERNETES", logo: "/images/kubernetes.png" },
    { name: "VEMBU", logo: "/images/vembu.png" }
  ];

  // 2. Références Internationales (8 premiers + Concentrix séparé plus bas)
  const internationalCompanies = [
    { name: "FAURECIA", logo: "/images/faurecia.png", url: "" },
    { name: "Nokia Solutions and Networks", logo: "/images/NOKIA.png", url: "" },
    { name: "Concentrix", logo: "/images/cocen.png", url: "" },
    { name: "Arles", logo: "/images/arles.jpeg", url: "" },
    { name: "Tulintech", logo: "/images/tulintech.png", url: "" },
    { name: "Timab", logo: "/images/tim.png", url: "" },

  ];

  // 3. Références Internationales (Pays)
  const internationalCountries = [
    { name: t.home.countries.ly, code: "ly" },
    { name: t.home.countries.cg, code: "cg" },

    { name: t.home.countries.dz, code: "dz" },
    { name: t.home.countries.cz, code: "cz" },
    { name: t.home.countries.ae, code: "ae" }, 
    { name: t.home.countries.jo, code: "jo" }  
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pRes, cRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/partners?size=100`),
          axios.get(`${API_BASE_URL}/api/clients?size=100`)
        ]);
        setPartners(pRes.data.content || pRes.data || []);
        setClients(cRes.data.content || cRes.data || []);
      } catch (err) { console.error("Erreur:", err); } 
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API_BASE_URL}/api/contact/public`, formData);
      setStatus({ type: 'success', msg: t.home.contact.success });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      setStatus({ type: 'danger', msg: t.home.contact.error });
    } finally { setSending(false); }
  };

  return (
    <div className="home-page">


     {/* --- BLOC SEO GOOGLE --- */}
      <Helmet>
        <title>{t.home.seo.title}</title>
        <meta name="description" content={t.home.seo.description} />
        <link rel="canonical" href="https://www.s2i.com.tn/" />
      </Helmet>
      {/* ------------------------ */}

      
      {/* 1. HERO SECTION */}
      <section className="hero-section-premium">
        <ParticlesBackground />
        <Container className="text-center position-relative" style={{ zIndex: 10 }}>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <div className="hero-tag">{t.home.hero.tag}</div>


           
            <div className="hero-experience-label">
            {lang === 'fr' ? "Plus de 30 années d'expertise" : "Over 30 years of expertise"}
            </div>                


            <h1 className="hero-title" dangerouslySetInnerHTML={{ __html: t.home.hero.title }}></h1>
            <p className="hero-subtitle">{t.home.hero.subtitle}</p>
            <Button variant="danger" className="btn-sii-glow mt-4" onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}>
              {t.home.hero.cta} <FaArrowRight className="ms-2" />
            </Button>
          </motion.div>
        </Container>
      </section>

      {/* 2. SERVICES */}
      <section className="py-5 bg-white">
        <Container>
          <Row className="g-4">
            {[
              { icon: <FaChartLine />, title: t.home.services.s1.title, desc: t.home.services.s1.desc },
              { icon: <FaCogs />,      title: t.home.services.s2.title, desc: t.home.services.s2.desc },
              { icon: <FaHeadset />,   title: t.home.services.s3.title, desc: t.home.services.s3.desc },
              { icon: <FaSimCard />,   title: t.home.services.s4.title, desc: t.home.services.s4.desc },
            ].map((s, i) => (
              <Col md={3} key={i}>
                <motion.div whileHover={{ y: -10 }} className="service-card-modern shadow-sm">
                  <div className="icon-circle">{s.icon}</div>
                  <h4>{s.title}</h4>
                  <p className="text-muted">{s.desc}</p>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* 3. PARTENAIRES */}
      <section className="py-5 section-dark-gradient overflow-hidden" ref={setPartnersRef}>
        <div className="text-center mb-5">
          <h2 className="title-modern text-white" dangerouslySetInnerHTML={{ __html: t.home.partners }}></h2>
          <p className="text-muted-light">{t.home.partners_sub}</p>
        </div>
        {loading ? <div className="text-center py-5"><Spinner animation="border" variant="danger" /></div> : <PartnerCarousel partners={partners} />}
      </section>



      {/* 4. CLIENTS */}
<section className="py-5 section-dark-gradient" ref={setClientsRef}>
  <Container>
    <div className="text-center mb-5">
      <h2 className="title-modern text-white" dangerouslySetInnerHTML={{ __html: t.home.clients }}></h2>
      <p className="text-muted-light">{t.home.clients_sub}</p>
    </div>

    {/* Grille principale (tous sauf les 4 derniers) */}
    <div className="client-grid-elegant mb-4">
      {clients.slice(0, -4).map((c, i) => (
        <motion.div key={c.id || i} whileHover={{ y: -10, scale: 1.03 }} className="client-card-solid">
          {c.websiteUrl ? (
              <a href={c.websiteUrl} target="_blank" rel="noopener noreferrer" className="card-link-wrapper">
                  <img src={getFullImageUrl(c.logo, c.name)} alt={c.name} onError={(e) => handleImageError(e, c.name)} />
              </a>
          ) : (
              <img src={getFullImageUrl(c.logo, c.name)} alt={c.name} onError={(e) => handleImageError(e, c.name)} />
          )}
          <div className="client-info-pop">{c.name}</div>
        </motion.div>
      ))}
    </div>

    {/* Ligne centrée pour les 4 derniers clients */}
    <div className="client-grid-centered">
      {clients.slice(-4).map((c, i) => (
        <motion.div key={c.id || `last-${i}`} whileHover={{ y: -10, scale: 1.03 }} className="client-card-solid">
          {c.websiteUrl ? (
              <a href={c.websiteUrl} target="_blank" rel="noopener noreferrer" className="card-link-wrapper">
                  <img src={getFullImageUrl(c.logo, c.name)} alt={c.name} onError={(e) => handleImageError(e, c.name)} />
              </a>
          ) : (
              <img src={getFullImageUrl(c.logo, c.name)} alt={c.name} onError={(e) => handleImageError(e, c.name)} />
          )}
          <div className="client-info-pop">{c.name}</div>
        </motion.div>
      ))}
    </div>
  </Container>
</section>



      {/* 4.5 CERTIFICATIONS */}
      <section className="py-5 section-dark-gradient" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }} ref={setCertifRef}>
        <Container>
          <div className="text-center mb-5">
            <h2 className="title-modern text-white" dangerouslySetInnerHTML={{ __html: t.home.certifications }}></h2>
            <p className="text-muted-light">{t.home.certifications_sub}</p>
          </div>
          
          <div className="cert-flex-center">
            {certifications.map((cert, i) => (
              <motion.div 
                key={i} 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -10, scale: 1.05 }} 
                className="cert-card-large"
              >
                <img src={cert.logo} alt={cert.name} onError={(e) => handleImageError(e, cert.name)} />
                <div className="client-info-pop">{cert.name}</div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* 4.6 RÉFÉRENCES INTERNATIONALES (MODIFIÉ POUR CENTRER LE DERNIER) */}
     {/* 4.6 RÉFÉRENCES INTERNATIONALES */}
<section className="py-5 section-dark-gradient" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }} ref={setInterRef}>
  <Container>
    <div className="text-center mb-5">
      <h2 className="title-modern text-white" dangerouslySetInnerHTML={{ __html: t.home.international_title }}></h2>
      <p className="text-muted-light">{t.home.international_sub}</p>
    </div>

    {/* Première ligne : sans URL */}
<Row className="justify-content-center g-4 mb-4">
  {internationalCompanies.slice(0, 3).map((comp, i) => (
    <Col key={i} lg={4} md={6}>
      <motion.div
        whileHover={{ y: -10 }}
        className="inter-card-horizontal"
        style={{ cursor: 'default' }}
      >
        <div className="accent-bar"></div>
        <div className="logo-container-wide">
          <img
            src={comp.logo}
            alt={comp.name}
            style={{ width: 'auto', height: 'auto', maxWidth: '90%', maxHeight: '70%' }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src =
                "https://via.placeholder.com/200x80?text=" + comp.name;
            }}
          />
        </div>
        <div className="client-info-pop">{comp.name}</div>
      </motion.div>
    </Col>
  ))}
</Row>
    {/* DEUXIÈME LIGNE : Les 3 derniers CENTRÉS et SANS URL */}
    <Row className="justify-content-center g-4">
  {internationalCompanies.slice(3).map((comp, i) => (
    <Col key={i + 3} lg={4} md={6}>
      <motion.div 
        whileHover={{ y: -10 }} 
        className="inter-card-horizontal"
        style={{ cursor: 'default' }}
      >
        <div className="accent-bar"></div>
        <div className="logo-container-wide">
          {/* On utilise comp.logo directement ici pour éviter le bug de l'URL distante */}
          <img 
            src={comp.logo} 
            alt={comp.name} 
            style={{ width: 'auto', height: 'auto', maxWidth: '90%', maxHeight: '70%' }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "https://via.placeholder.com/200x80?text=" + comp.name;
            }} 
          />
        </div>
        <div className="client-info-pop">{comp.name}</div>
      </motion.div>
    </Col>
  ))}
</Row>

          {/* LA LISTE DES PAYS */}
          <div className="text-center mt-5">
            <div className="d-flex align-items-center justify-content-center mb-4">
                <div className="line-red"></div>
                <h5 className="text-white px-3 fw-light text-uppercase" style={{ letterSpacing: '3px', fontSize: '0.85rem' }}>
                    {t.home.realized_in}
                </h5>
                <div className="line-red"></div>
            </div>
            <div className="d-flex flex-wrap justify-content-center gap-3">
                {internationalCountries.map((country, i) => (
                    <motion.div 
                        key={i}
                        whileHover={{ scale: 1.1, backgroundColor: '#d90000' }}
                        className="country-pill"
                    >
                        <img 
                            src={`https://flagcdn.com/w40/${country.code}.png`} 
                            alt={country.name}
                            className="me-2 shadow-sm"
                        />
                        <span>{country.name}</span>
                    </motion.div>
                ))}
            </div>
          </div>
        </Container>
      </section>

      {/* 5. POURQUOI CHOISIR S2I */}
      <section style={{ 
  background: 'linear-gradient(180deg, #0f172a 0%, #020617 100%)', 
  padding: '100px 0', 
  color: 'white' 
}}>
  <div className="container">
    <div style={{ textAlign: 'center', marginBottom: '60px' }}>
      <h3 style={{ fontWeight: 800, fontSize: '2.8rem', letterSpacing: '-1px' }}>
  {lang === 'fr' ? 'Pourquoi choisir ' : 'Why choose '}
  <span style={{ color: '#d90000' }}>S2i</span> ?
</h3>
      <div style={{ width: '60px', height: '4px', background: '#d90000', margin: '20px auto' }}></div>
    </div>

    <Row className="g-5">
      {t.home.why.stats.map((item, index) => (
        <Col lg={3} md={6} key={index}>
          <div className="stat-card-premium">
            {/* L'ICONE : Blanche avec un cercle dégradé rouge autour */}
            <div className="icon-wrapper-premium">
              {index === 0 && <FaTrophy />}
              {index === 1 && <FaBullseye />}
              {index === 2 && <FaUsers />}
              {index === 3 && <FaRocket />}
            </div>

            <h4 className="stat-number">
              <Counter value={item.val} />+
            </h4>
            
            <h5 className="stat-label">{item.lab}</h5>
            
            <p className="stat-desc">{item.desc}</p>
          </div>
        </Col>
      ))}
    </Row>
  </div>

  <style>{`
    .stat-card-premium {
      text-align: center;
      padding: 20px;
      transition: 0.4s;
    }
    .icon-wrapper-premium {
      width: 80px;
      height: 80px;
      background: linear-gradient(135deg, rgba(217,0,0,0.2) 0%, rgba(217,0,0,0) 100%);
      border: 1px solid rgba(217,0,0,0.3);
      border-radius: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 25px;
      font-size: 2rem;
      color: #ffffff; /* Icône blanche pour la classe */
      transition: 0.3s;
    }
    .stat-card-premium:hover .icon-wrapper-premium {
      background: #d90000;
      transform: translateY(-10px);
      box-shadow: 0 10px 30px rgba(217,0,0,0.4);
    }
    .stat-number {
      font-weight: 800;
      font-size: 2.5rem;
      margin-bottom: 5px;
      background: linear-gradient(180deg, #fff 0%, #cbd5e1 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
    .stat-label {
      font-weight: 700;
      font-size: 0.9rem;
      text-transform: uppercase;
      letter-spacing: 1px;
      color: #ffffff;
      margin-bottom: 15px;
    }
    .stat-desc {
      color: #94a3b8;
      font-size: 0.9rem;
      line-height: 1.6;
    }



    /* Nouvelle classe pour centrer les derniers éléments */
.client-grid-centered {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 25px;
}

/* On s'assure que les cartes gardent la même taille que dans la grille */
.client-grid-centered .client-card-solid {
    width: 180px; /* Même largeur que le minmax de votre grille */
}

/* Optionnel : Ajustement mobile pour que les 4 derniers se comportent comme les autres */
@media (max-width: 576px) {
    .client-grid-centered .client-card-solid {
        width: calc(50% - 13px); /* 2 colonnes sur mobile comme la grille */
    }
}




  `}</style>
</section>
      {/* 6. CONTACT */}
      <section id="contact" className="py-5 bg-light">
        <Container>
          <Row className="g-0 shadow-lg rounded-5 overflow-hidden bg-white">
            <Col lg={5} className="p-5">
              <h3 className="fw-bold mb-2">{t.home.contact.title}</h3>
              <div className="contact-info-block mb-4">
                <p className="mb-1">Rue des Entrepreneurs, Immeubles Hertz Bloc B 1er etage, 2035 Charguia II, Tunisie</p>
                <p className="mb-0"><a href="mailto:s2iteam@s2i.com.tn" className="contact-email">s2iteam@s2i.com.tn</a></p>
              </div>
              <AnimatePresence>
                {status.msg && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <Alert variant={status.type} dismissible onClose={() => setStatus({ type: '', msg: '' })}>
                      {status.msg}
                    </Alert>
                  </motion.div>
                )}
              </AnimatePresence>
              <Form onSubmit={handleSubmit}>
                <Form.Control name="name" value={formData.name} onChange={handleChange} className="form-modern-input" placeholder={t.home.contact.name_ph} required />
                <Form.Control name="email" type="email" value={formData.email} onChange={handleChange} className="form-modern-input" placeholder={t.home.contact.email_ph} required />
                <Form.Control name="phone" type="tel" value={formData.phone} onChange={handleChange} className="form-modern-input" placeholder={t.home.contact.phone_ph} />
                <Form.Control name="message" as="textarea" rows={3} value={formData.message} onChange={handleChange} className="form-modern-input" placeholder={t.home.contact.msg_ph} required />
                <Button type="submit" variant="danger" className="w-100 py-3 fw-bold rounded-pill" disabled={sending}>
                  {sending ? t.home.contact.sending : t.home.contact.send_btn}
                </Button>
              </Form>
            </Col>
            <Col lg={7}>
              <iframe title="S2I Map" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d790!2d10.2066663!3d36.8549135!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12fd34d37ed015ad%3A0x629bd63e41a0132a!2sS2i!5e0!3m2!1sfr!2stn!4v1715852324541!5m2!1sfr!2stn" width="100%" height="100%" style={{ border: 0, minHeight: '400px' }} allowFullScreen="" loading="lazy"></iframe>
            </Col>
          </Row>
        </Container>
      </section>

      <style>{`
        .home-page { font-family: 'Poppins', sans-serif; }
        .hero-subtitle {
  /* On garde la taille d'origine */
  font-size: 1.1rem; 
  /* On applique la nouvelle couleur recommandée (gris argenté doux) */
  color: #cbd5e1; 
  /* On remet l'opacité à 1 pour la lisibilité, mais la couleur fait le travail de douceur */
  opacity: 1; 
  /* On ne touche pas au font-family ni au font-weight pour garder le côté "classe" */
  margin-top: 20px;
}


        .hero-experience-label {
  color: #cbd5e1;           /* Gris argenté (reposant et lisible) */
  font-size: 0.95rem;       /* Taille raffinée */
  font-weight: 400;         /* Plus léger pour contraster avec le 900 du titre */
  text-transform: uppercase;
  letter-spacing: 5px;      /* Très important pour le côté "Luxe/Expert" */
  margin-bottom: 5px;
  margin-top: 15px;
  display: block;
  font-family: 'Poppins', sans-serif;
}
        .hero-section-premium { background: #0f172a; padding: 120px 0; color: white; position: relative; }
        .hero-title { font-size: clamp(2.5rem, 7vw, 4.5rem); font-weight: 900; line-height: 1.1; margin-top: 10px; }
        .hero-title span { color: #d90000; }
        .hero-tag { display: inline-block; padding: 5px 20px; background: rgba(217,0,0,0.1); border: 1px solid #d90000; border-radius: 50px; color: #d90000; font-weight: 700; margin-bottom: 20px; }
        .btn-sii-glow { padding: 15px 40px; border-radius: 50px; font-weight: 700; box-shadow: 0 0 20px rgba(217,0,0,0.4); }
        .section-dark-gradient { background: linear-gradient(145deg, #0f172a 0%, #1e293b 100%); padding: 80px 0; }
        .text-muted-light { 
        color: #cbd5e1; /* Un gris argenté plus clair et net */
        font-size: 1.1rem;
        letter-spacing: 0.02rem;
        }        
        .title-modern { font-size: 2.5rem; font-weight: 800; }
        .title-modern span { color: #d90000; }
        .service-card-modern { background: white; padding: 40px 20px; border-radius: 30px; text-align: center; height: 100%; transition: 0.3s; }
        .icon-circle { width: 70px; height: 70px; background: #fef2f2; color: #d90000; display: flex; align-items: center; justify-content: center; border-radius: 20px; margin: 0 auto 20px; font-size: 2rem; }
        
        .client-grid-elegant { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 25px; }
        .client-card-solid { background: #ffffff; height: 120px; border-radius: 25px; display: flex; align-items: center; justify-content: center; position: relative; padding: 20px; box-shadow: 0 10px 30px rgba(0,0,0,0.2); cursor: pointer; transition: 0.3s; }
        .client-card-solid img { max-width: 85%; max-height: 65%; object-fit: contain; }
        
        .cert-flex-center { display: flex; flex-wrap: wrap; justify-content: center; gap: 30px; }
        .cert-card-large { background: #ffffff; height: 150px; width: 230px; border-radius: 30px; display: flex; align-items: center; justify-content: center; position: relative; padding: 25px; box-shadow: 0 12px 35px rgba(0,0,0,0.3); cursor: pointer; transition: 0.3s; }
        .cert-card-large img { max-width: 90%; max-height: 75%; object-fit: contain; }

        .inter-card-link-wrapper { text-decoration: none !important; display: flex; width: 100%; height: 100%; }
        .inter-card-horizontal { background: #ffffff; width: 100%; height: 110px; border-radius: 16px; display: flex; align-items: center; justify-content: center; position: relative; overflow: hidden; padding: 15px 25px; box-shadow: 0 10px 30px rgba(0,0,0,0.3); transition: all 0.3s ease; }
        .logo-container-wide { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; }
        .logo-container-wide img { max-width: 90%; max-height: 70%; object-fit: contain; }
        .accent-bar { position: absolute; left: 0; top: 0; bottom: 0; width: 6px; background: #d90000; }
        .inter-card-link-wrapper:hover .inter-card-horizontal { box-shadow: 0 20px 40px rgba(217, 0, 0, 0.25); }

        .country-pill { background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1); padding: 10px 25px; border-radius: 100px; color: white; display: flex; align-items: center; font-weight: 500; transition: 0.3s; font-size: 0.9rem; cursor: default; }
        .country-pill img { width: 24px; height: auto; border-radius: 3px; }
        .line-red { width: 40px; height: 1px; background: #d90000; }

        .client-info-pop { position: absolute; bottom: 10px; background: #d90000; color: white; padding: 2px 10px; border-radius: 8px; font-size: 0.7rem; font-weight: 700; opacity: 0; transition: 0.3s; }
        .client-card-solid:hover .client-info-pop, .cert-card-large:hover .client-info-pop, .inter-card-horizontal:hover .client-info-pop { opacity: 1; bottom: 15px; }
        
        .contact-info-block { font-size: 0.9rem; color: #475569; line-height: 1.8; }
        .contact-email { color: #d90000; font-weight: 600; text-decoration: none; }
        .form-modern-input { border-radius: 12px; padding: 12px; border: 1px solid #e2e8f0; margin-bottom: 20px; }
        .card-link-wrapper { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; text-decoration: none; }
      `}</style>
    </div>
  );
};

const getFullImageUrl = (path, name) => {
  if (!path) return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'S2I')}&background=f1f5f9&color=d90000&size=128&bold=true`;
  if (path.startsWith('http')) return path;
  return `${API_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
};

const handleImageError = (e, name) => {
  e.target.onerror = null; 
  e.target.src = `https://via.placeholder.com/250/ffffff/d90000?text=${encodeURIComponent(name || 'S2I')}`;
};

const PartnerCarousel = ({ partners = [] }) => {
  const [current, setCurrent] = useState(0);
  const next = useCallback(() => setCurrent((p) => (p + 1) % partners.length), [partners.length]);
  const prev = useCallback(() => setCurrent((p) => (p - 1 + partners.length) % partners.length), [partners.length]);
  if (partners.length === 0) return null;
  const displayItems = [partners[(current - 1 + partners.length) % partners.length], partners[current], partners[(current + 1) % partners.length]];
  return (
    <div className="partner-carousel-wrapper">
      <div className="d-flex justify-content-center align-items-center gap-3 gap-md-5">
        <button className="nav-arrow-sii" onClick={prev}><FaChevronLeft /></button>
        {displayItems.map((p, i) => (
          <motion.div key={i} animate={{ scale: i === 1 ? 1.2 : 0.8, opacity: i === 1 ? 1 : 0.5 }} className="partner-card-solid">
            {p?.websiteUrl ? (
              <a href={p.websiteUrl} target="_blank" rel="noopener noreferrer" className="card-link-wrapper">
                <img src={getFullImageUrl(p?.logo, p?.name)} alt={p?.name} onError={(e) => handleImageError(e, p?.name)} />
              </a>
            ) : (
              <img src={getFullImageUrl(p?.logo, p?.name)} alt={p?.name} onError={(e) => handleImageError(e, p?.name)} />
            )}
          </motion.div>
        ))}
        <button className="nav-arrow-sii" onClick={next}><FaChevronRight /></button>
      </div>
      <style>{`
        .partner-card-solid { width: 300px; height: 180px; background: white; border-radius: 30px; display: flex; align-items: center; justify-content: center; padding: 30px; box-shadow: 0 20px 40px rgba(0,0,0,0.3); }
        .partner-card-solid img { max-width: 90%; max-height: 80%; object-fit: contain; }
        .nav-arrow-sii { width: 50px; height: 50px; border-radius: 50%; border: none; background: white; color: #d90000; display: flex; align-items: center; justify-content: center; box-shadow: 0 10px 20px rgba(0,0,0,0.2); transition: 0.3s; cursor: pointer; }
        .nav-arrow-sii:hover { background: #d90000; color: white; }
        @media (max-width: 768px) { .partner-card-solid { width: 180px; height: 120px; } }
      `}</style>
    </div>
  );
};

export default Home;